import { findLastCountableChange } from '../Datasource/CountableChanges/CountableChangeDatasource';
import { type AircraftCountableType, type CountableChange } from '../Datasource/CountableChanges/types';
import { COUNTABLE_CHANGE_UPLIFT, COUNTABLE_TYPES_ADDITIVE } from '../Datasource/CountableChanges/types';
import { type FinishedFlightLeg, type FlightLeg } from '../Datasource/FlightLegs/FlightLeg';
import { compareLegsInFlight, isLegFinished } from '../Datasource/FlightLegs/FlightLeg';
import { getDbSyncer } from '../Datasource/LocalDB/DbSyncer';
import { getDb } from '../Datasource/LocalDB/LocalDB';
import { toMap } from './arrayUtils';

type LegAndCcs = {
    leg: FlightLeg;
    uplift: CountableChange;
    consumption: CountableChange;
};

/**
 * This function ensures that within a flight, in a portion from start to first unifinshed leg (which before closing
 * flight must be whole flight) there are no discontinuities in additive meters, like landings and cylcles.
 * ChangeValues are considered correct; the first leg continues from the aircraft's last change before the flight (a
 * previous leg, or a correction or technical uplift, which are never changed here).
 * Remaining values are computed for continuity (so when user changes 1st leg's landings, they are incremented on 2nd and 3rd leg)
 *
 * For cross-filling of upliftables (fuel etc.) from uplift after to consumption before, see finishedLegToFinishLegState()
 */
export async function ensureContinuityInFlight(flightId: string) {
    const db = getDb();
    const [legs, countableChanges] = await Promise.all([
        db.legs.where({ flightId }).toArray(),
        db.countableChanges.where({ flightId }).toArray(),
    ]);
    const validLegs = legs.filter((l) => !l.deleted && l.timeOut);
    validLegs.sort(compareLegsInFlight);
    const firstUnfinishedIndex = validLegs.findIndex((l) => !isLegFinished(l));
    const unfinishedLength = firstUnfinishedIndex === -1 ? undefined : firstUnfinishedIndex;
    const finishedLegs = validLegs.slice(0, unfinishedLength) as FinishedFlightLeg[];
    const ccsMap = toMap(countableChanges);
    const legsAndCcs: LegAndCcs[] = finishedLegs.map((leg): LegAndCcs => {
        const uplift = ccsMap.get(leg.upliftCountableChangeId);
        if (uplift === undefined || uplift.deleted) {
            throw new Error(`Cannot find uplift CC for flight to ensure continuity`);
        }
        const consumption = ccsMap.get(leg.consumptionCountableChangeId);
        if (consumption === undefined || consumption.deleted) {
            throw new Error(`Cannot find consumption CC for flight to ensure continuity`);
        }
        return { leg, uplift, consumption };
    });
    const changedCountableChanges = new Set<CountableChange>();
    for (let i = 0; i < legsAndCcs.length; i++) {
        const thisLeg = legsAndCcs[i];
        const previous = i > 0 ? legsAndCcs[i - 1].consumption : await getLastCountableChangeBeforeFlight(thisLeg.leg);
        if (!previous) {
            continue;
        }
        const changeOccuredThisUplift = transferFromAfterToBefore(previous, thisLeg.uplift, COUNTABLE_TYPES_ADDITIVE);
        if (changeOccuredThisUplift) {
            changedCountableChanges.add(thisLeg.uplift);
        }
        const changeOccuredThisConsumption = transferFromAfterToBefore(
            thisLeg.uplift,
            thisLeg.consumption,
            COUNTABLE_TYPES_ADDITIVE
        );
        if (changeOccuredThisConsumption) {
            changedCountableChanges.add(thisLeg.consumption);
        }
    }
    const changedCountableChangesArray = [...changedCountableChanges.values()];
    if (changedCountableChangesArray.length) {
        await db.countableChanges.bulkPut(changedCountableChangesArray);
        const ids = changedCountableChangesArray.map((cc) => cc.id);
        await getDbSyncer().markToSync(db.countableChanges, ...ids);
    }
}

function transferFromAfterToBefore(
    sourceCc: CountableChange,
    targetCc: CountableChange,
    types: AircraftCountableType[]
): boolean {
    let changeOccured = false;
    for (const type of types) {
        const valueIndexSource = sourceCc.countableTypes.indexOf(type);
        const valueIndexTarget = targetCc.countableTypes.indexOf(type);
        if (valueIndexSource >= 0 && valueIndexTarget >= 0) {
            const sourceAfterValue = sourceCc.countableValuesAfter[valueIndexSource];
            const targetBeforeValue = targetCc.countableValuesBefore[valueIndexTarget];
            if (sourceAfterValue !== targetBeforeValue) {
                changeOccured = true;
                targetCc.countableValuesBefore[valueIndexTarget] = sourceAfterValue;
                const changeIndex = targetCc.changeTypes.indexOf(type);
                const changeValue = targetCc.changeValues[changeIndex] || 0;
                targetCc.countableValuesAfter[valueIndexTarget] = sourceAfterValue + changeValue;
            }
        }
    }
    return changeOccured;
}

/** A leg's uplift as the last change before the flight means that leg's consumption isn't on the device yet. */
async function getLastCountableChangeBeforeFlight(firstLeg: FlightLeg): Promise<CountableChange | undefined> {
    const previous = await findLastCountableChange(firstLeg.aircraftId, firstLeg.timeOut);

    return previous?.legId && previous.type === COUNTABLE_CHANGE_UPLIFT ? undefined : previous;
}
